import { interfaceKeys } from 'src/app/common/app.helpers';
import {
  MapStatisticsIcon,
  MapStatisticsIconsLookup,
  MapStatisticsTabsLookup,
  OverallStatistic,
} from 'src/app/components/map/types/custom.types';
import FieldAllowedAttributeTypes from 'src/app/forms/fields/field-allowed-attribute-types.values';
import FieldAllowedValueClasses from 'src/app/forms/fields/field-allowed-value-classes.values';
import { SearchFormTypes } from 'src/app/forms/forms.enum';
import { Attribute } from 'src/app/interfaces/form-and-field.interface';
import {
  CovidStatistics,
  MapGotoBlockForm,
  MapSearchBlockForm,
  MapState,
  OverallStatistics,
} from 'src/app/interfaces/map.interface';
import { Coords } from '../../swagger-api';
import { SearchCategories } from '../search/search.enum';
import { MapFilters, NestedBucketIndividualRole, NestedBucketOrganizationType } from './map.enum';
import { FieldValue, SearchBlockFieldValues } from './map.interfaces';
import MapSettings from './map.settings';

export function shouldDisableField(fieldName: keyof SearchBlockFieldValues, activeField: FieldValue): boolean {
  const FIELD_CONFLICTS: Record<keyof SearchBlockFieldValues, (keyof SearchBlockFieldValues)[]> = {
    diseases: ['charity_focuses'],
    specialties: ['charity_focuses'],
    charity_focuses: ['specialties', 'diseases'],
    roles: [],
  };

  return (
    FIELD_CONFLICTS[fieldName]?.includes(activeField.field as keyof SearchBlockFieldValues) &&
    !!activeField.selectedItems.length
  );
}
export class MapValues {
  public static rolesFieldName = 'roles';
  public static keywordFieldName = 'keyword';
  public static diseasesFieldName = 'diseases';
  public static specialtiesFieldName = 'specialties';
  public static charityFocus = 'charity_focuses';

  public static mapStatisticsSearchIndividualRolesKeys: string[] = [
    SearchCategories.DOCTORS,
    SearchCategories.MED_STAFF,
    SearchCategories.PATIENTS,
    SearchCategories.RESEARCHERS,
  ];

  public static mapStatisticsTabsLookup: MapStatisticsTabsLookup = {
    [OverallStatistic.Covid]: ['cases', 'vaccinations', 'deaths'],
    [OverallStatistic.Communities]: ['communities_count', 'community_leads_count', 'community_participants_count'],
    [OverallStatistic.Projects]: ['projects_count', 'project_leads_count', 'project_participants_count'],
    [OverallStatistic.Individuals]: [
      'total_individuals_count',
      'patients_count',
      'doctors_count',
      'medstaff_count',
      'researchers_count',
    ],
    [OverallStatistic.Organizations]: [
      'total_organizations_count',
      'foundation_organizations_count',
      'institute_organizations_count',
      'hospital_organizations_count',
      'research_organizations_count',
    ],
  };

  public static mapStatisticsIcons: { [key: string]: MapStatisticsIcon } = {
    communitiesCircle: {
      path: 'assets/icons/circle/icon-circle-community.svg',
      class: 'icon-circle-community',
    },
    connectionsCircle: {
      path: 'assets/icons/circle/icon-circle-connections.svg',
      class: 'icon-circle-connections',
    },
    projectCircle: {
      path: 'assets/icons/circle/icon-circle-project.svg',
      class: 'icon-circle-project',
    },
    patientCircle: {
      path: 'assets/icons/circle/icon-circle-patient.svg',
      class: 'icon-circle-patient',
    },
    doctorCircle: {
      path: 'assets/icons/circle/icon-circle-doctor.svg',
      class: 'icon-circle-doctor',
    },
    organizationCircle: {
      path: 'assets/icons/circle/icon-circle-organization.svg',
      class: 'icon-circle-organization',
    },
    researcherCircle: {
      path: 'assets/icons/circle/icon-circle-researcher.svg',
      class: 'icon-circle-researcher',
    },
    memoriamCircle: {
      path: 'assets/icons/circle/icon-circle-in-memoriam.svg',
      class: 'icon-circle-in-memoriam',
    },
    injectionCircle: {
      path: 'assets/icons/circle/icon-circle-injection.svg',
      class: 'icon-circle-injection',
    },
    medicalStaffCircle: {
      path: 'assets/icons/circle/icon-circle-medstaff.svg',
      class: 'icon-circle-medstaff',
    },
  };

  public static mapStatisticsIconsLookup: MapStatisticsIconsLookup = {
    cases: {
      title: ['MAP_STATISTICS_TITLE_COVID_CASES', 'MAP_STATISTICS_TITLE_COVID_CASE'],
      icon: MapValues.mapStatisticsIcons['patientCircle'],
    },
    vaccinations: {
      title: ['MAP_STATISTICS_TITLE_COVID_VACCINATIONS', 'MAP_STATISTICS_TITLE_COVID_VACCINATION'],
      icon: MapValues.mapStatisticsIcons['injectionCircle'],
    },
    tests: {
      title: ['MAP_STATISTICS_TITLE_COVID_TESTED', 'MAP_STATISTICS_TITLE_COVID_TESTED_SINGULAR'],
      icon: MapValues.mapStatisticsIcons['researcherCircle'],
    },
    deaths: {
      title: ['MAP_STATISTICS_TITLE_COVID_DEATHS', 'MAP_STATISTICS_TITLE_COVID_DEATH'],
      icon: MapValues.mapStatisticsIcons['memoriamCircle'],
    },
    communities_count: {
      title: ['MAP_STATISTICS_TITLE_COMMUNITIES', 'MAP_STATISTICS_TITLE_COMMUNITY'],
      icon: MapValues.mapStatisticsIcons['communitiesCircle'],
    },
    community_leads_count: {
      title: ['MAP_STATISTICS_TITLE_COMMUNITY_LEADS', 'MAP_STATISTICS_TITLE_COMMUNITY_LEAD'],
      icon: MapValues.mapStatisticsIcons['patientCircle'],
    },
    community_participants_count: {
      title: ['MAP_STATISTICS_TITLE_COMMUNITY_PARTICIPANTS', 'MAP_STATISTICS_TITLE_COMMUNITY_PARTICIPANT'],
      icon: MapValues.mapStatisticsIcons['connectionsCircle'],
    },
    projects_count: {
      title: ['MAP_STATISTICS_TITLE_PROJECTS', 'MAP_STATISTICS_TITLE_PROJECT'],
      icon: MapValues.mapStatisticsIcons['projectCircle'],
    },
    project_leads_count: {
      title: ['MAP_STATISTICS_TITLE_PROJECT_LEADS', 'MAP_STATISTICS_TITLE_PROJECT_LEAD'],
      icon: MapValues.mapStatisticsIcons['patientCircle'],
    },
    project_participants_count: {
      title: ['MAP_STATISTICS_TITLE_PROJECT_PARTICIPANTS', 'MAP_STATISTICS_TITLE_PROJECT_PARTICIPANT'],
      icon: MapValues.mapStatisticsIcons['connectionsCircle'],
    },
    total_organizations_count: {
      title: ['MAP_STATISTICS_TITLE_ORGANIZATIONS', 'MAP_STATISTICS_TITLE_ORGANIZATION'],
      icon: MapValues.mapStatisticsIcons['organizationCircle'],
    },
    foundation_organizations_count: {
      title: ['MAP_STATISTICS_TITLE_FOUNDATIONS', 'MAP_STATISTICS_TITLE_FOUNDATION'],
      icon: MapValues.mapStatisticsIcons['organizationCircle'],
    },
    institute_organizations_count: {
      title: ['MAP_STATISTICS_TITLE_INSTITUTES', 'MAP_STATISTICS_TITLE_INSTITUTE'],
      icon: MapValues.mapStatisticsIcons['organizationCircle'],
    },
    hospital_organizations_count: {
      title: ['MAP_STATISTICS_TITLE_HOSPITALS', 'MAP_STATISTICS_TITLE_HOSPITAL'],
      icon: MapValues.mapStatisticsIcons['organizationCircle'],
    },
    research_organizations_count: {
      title: ['MAP_STATISTICS_TITLE_RESEARCH_CENTERS', 'MAP_STATISTICS_TITLE_RESEARCH_CENTER'],
      icon: MapValues.mapStatisticsIcons['organizationCircle'],
    },
    total_individuals_count: {
      title: ['MAP_STATISTICS_TITLE_INDIVIDUALS', 'MAP_STATISTICS_TITLE_INDIVIDUAL'],
      icon: MapValues.mapStatisticsIcons['communitiesCircle'],
    },
    patients_count: {
      title: ['MAP_STATISTICS_TITLE_PATIENTS', 'MAP_STATISTICS_TITLE_PATIENT'],
      icon: MapValues.mapStatisticsIcons['patientCircle'],
    },
    doctors_count: {
      title: ['MAP_STATISTICS_TITLE_DOCTORS', 'MAP_STATISTICS_TITLE_DOCTOR'],
      icon: MapValues.mapStatisticsIcons['doctorCircle'],
    },
    medstaff_count: {
      title: ['MAP_STATISTICS_TITLE_MEDICAL_STAFF', 'MAP_STATISTICS_TITLE_MEDICAL_STAFF'],
      icon: MapValues.mapStatisticsIcons['medicalStaffCircle'],
    },
    researchers_count: {
      title: ['MAP_STATISTICS_TITLE_RESEARCHERS', 'MAP_STATISTICS_TITLE_RESEARCHER'],
      icon: MapValues.mapStatisticsIcons['researcherCircle'],
    },
  };

  public static mapLocationMetadataAttrs: Attribute[] = [
    {
      name: 'form_location',
      value_class_front: FieldAllowedValueClasses.CITY_LEVEL_LOCATION,
      min_length: 1,
      max_length: 50,
      type_attribute: FieldAllowedAttributeTypes.TEXT,
      required: false,
      read_only: false,
      label: '',
      placeholder: 'MAP_SEARCH_BLOCK_LOCATION_FIELD_HINT',
      security: false,
      scheme_name: SearchFormTypes.MAP_GOTO_BLOCK,
    },
    {
      name: 'viewport',
      value_class_front: FieldAllowedValueClasses.VIEWPORT,
      min_length: 1,
      max_length: 50,
      type_attribute: FieldAllowedAttributeTypes.TEXT,
      required: false,
      read_only: true,
      label: 'Viewport',
      placeholder: 'Automatically by viewport',
      security: false,
      scheme_name: SearchFormTypes.MAP_GOTO_BLOCK,
    },
  ];

  public static mapNameMetadataAttrs: Attribute[] = [
    {
      name: MapValues.keywordFieldName,
      value_class_front: FieldAllowedValueClasses.CHARACTERS,
      min_length: 1,
      max_length: 50,
      counter_hide: true,
      type_attribute: FieldAllowedAttributeTypes.TEXT,
      required: false,
      read_only: false,
      label: '',
      placeholder: 'MAP_SEARCH_BLOCK_NAME_FIELD_HINT',
      security: false,
      scheme_name: SearchFormTypes.MAP_SEARCH_BLOCK,
    },
  ];

  public static mapPeopleRolesMetadataAttr: Attribute = {
    name: MapValues.rolesFieldName,
    value_class_front: FieldAllowedValueClasses.MAP_ROLES_SELECT,
    min_length: 1,
    max_length: 50,
    type_attribute: FieldAllowedAttributeTypes.MULTIPLE_SEARCH_SELECT,
    required: false,
    read_only: false,
    label: '',
    placeholder: 'MAP_SEARCH_BLOCK_ROLES_FIELD_HINT',
    security: false,
    scheme_name: SearchFormTypes.MAP_SEARCH_BLOCK,
  };

  public static mapCategoriesFieldMetadataAttrs: Attribute[] = [
    {
      name: MapValues.charityFocus,
      value_class_front: FieldAllowedValueClasses.MAP_CHARITY_FOCUSES_SELECT,
      min_length: 1,
      max_length: 2000,
      type_attribute: FieldAllowedAttributeTypes.MULTIPLE_SEARCH_SELECT,
      required: false,
      read_only: false,
      label: '',
      placeholder: 'MAP_SEARCH_BLOCK_CHARITY_FOCUS_FIELD_HINT',
      security: false,
      scheme_name: SearchFormTypes.MAP_SEARCH_BLOCK,
    },
    {
      name: MapValues.specialtiesFieldName,
      value_class_front: FieldAllowedValueClasses.MAP_SPECIALTIES_SELECT,
      min_length: 1,
      max_length: 2000,
      type_attribute: FieldAllowedAttributeTypes.MULTIPLE_SEARCH_SELECT,
      required: false,
      read_only: false,
      label: '',
      placeholder: 'MAP_SEARCH_BLOCK_SPECIALTIES_FIELD_HINT',
      security: false,
      scheme_name: SearchFormTypes.MAP_SEARCH_BLOCK,
    },
    {
      name: MapValues.diseasesFieldName,
      value_class_front: FieldAllowedValueClasses.MAP_DISEASES_SELECT,
      min_length: 1,
      max_length: 2000,
      type_attribute: FieldAllowedAttributeTypes.MULTIPLE_SEARCH_SELECT,
      required: false,
      read_only: false,
      label: '',
      placeholder: 'MAP_SEARCH_BLOCK_DISEASES_FIELD_HINT',
      security: false,
      scheme_name: SearchFormTypes.MAP_SEARCH_BLOCK,
    },
  ];

  public static defaultCovidStatistics: () => OverallStatistics = () => ({
    statistic_type: OverallStatistic.Covid,
    statistic_counters: {
      cases: 189185220,
      tests: 2856007325,
      deaths: 4074704,
    } as CovidStatistics,
  });

  public static mapSearchFormMetadataAttrs: () => Attribute[] = () => [
    ...MapValues.mapCategoriesFieldMetadataAttrs,
    ...MapValues.mapNameMetadataAttrs,
    MapValues.mapPeopleRolesMetadataAttr,
  ];

  public static mapGotoFormMetadataAttrs: () => Attribute[] = () => [...MapValues.mapLocationMetadataAttrs];

  public static searchBlockTitleMapper: {
    [key: string]: string;
  } = {
    [MapFilters.COMMUNITIES]: 'MAP_SEARCH_BLOCK_COMMUNITIES_TITLE_HINT',
    [MapFilters.PROJECTS]: 'MAP_SEARCH_BLOCK_PROJECTS_TITLE_HINT',
    [MapFilters.ORGANIZATIONS]: 'MAP_SEARCH_BLOCK_ORGANIZATIONS_TITLE_HINT',
    [MapFilters.PEOPLE]: 'MAP_SEARCH_BLOCK_PEOPLE_TITLE_HINT',
  };

  public static mapIndividualRolesKeys: string[] = [
    NestedBucketIndividualRole.PATIENT,
    NestedBucketIndividualRole.DOCTOR,
    NestedBucketIndividualRole.MED_STAFF,
    NestedBucketIndividualRole.RESEARCHER,
  ];

  public static mapOrganizationTypesKeys: string[] = [
    NestedBucketOrganizationType.FOUNDATION,
    NestedBucketOrganizationType.HOSPITAL,
    NestedBucketOrganizationType.INSTITUTE,
    NestedBucketOrganizationType.RESEARCH_CENTER,
    NestedBucketOrganizationType.OTHER,
  ];

  public static mapIndividualRolesKeysWithoutMedstaffAndPatiens: string[] = [
    NestedBucketIndividualRole.DOCTOR,
    NestedBucketIndividualRole.RESEARCHER,
  ];

  public static readonly initialCoords: Coords = { lat: 37.779584, lon: -122.419339 };

  public static defaultMapState: () => MapState = () => ({
    location: {
      center: MapValues.initialCoords,
      northWestCoords: null,
      southEastCoords: null,
    },
    zoom: MapSettings.INITIAL_MAP_ZOOM,
    filter: null,
  });

  public static mapSearchBlockFormKeys: string[] = interfaceKeys<MapSearchBlockForm>({
    keyword: 1,
    roles: 1,
    diseases: 1,
    specialties: 1,
    diseaseTitles: 1,
    specialtyTitles: 1,
    charity_focuses: 1,
    charityFocusesTitle: 1,
  });

  public static mapGotoBlockFormKeys: string[] = interfaceKeys<MapGotoBlockForm>({
    form_location: 1,
    pins_fetched: 1,
    viewport: 1,
  });
}
